import { RefObject, useLayoutEffect, useRef, useState } from "react";

/**
 * The rendered height of an element while it sits in the document flow.
 *
 * For a bar that swaps from in-flow to `position: fixed` once scrolled past:
 * the spacer that takes its place must be exactly as tall as the in-flow
 * version, or the page jumps when the swap happens. Measure while `inFlow`
 * is true and keep the last reading once the element leaves the flow.
 */
export const useInFlowHeight = <T extends HTMLElement = HTMLDivElement>(
  inFlow: boolean,
): [RefObject<T>, number | undefined] => {
  const ref = useRef<T>(null);
  const [height, setHeight] = useState<number>();

  useLayoutEffect(() => {
    if (!inFlow) return;
    const element = ref.current;
    if (!element) return;

    const measure = () => setHeight(element.getBoundingClientRect().height);
    measure();

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [inFlow]);

  return [ref, height];
};
